import React from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, CalendarClock, History } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAcademicYearSelection } from '@/contexts/AcademicYearContext';
import { YearState, UserRole } from '@/types';
import { isCoordinatorLikeRole } from '@/utils/roleAccess';

/**
 * Componente Header con breadcrumbs y acciones
 * Muestra la navegación actual y acciones del usuario
 */
const Header: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const {
    academicYears,
    selectedAcademicYearId,
    selectedAcademicYear,
    activeAcademicYear,
    canSelectHistoricalYear,
    setSelectedAcademicYearId,
  } = useAcademicYearSelection();

  const canUseYearSwitcher = Boolean(
    user?.role && (user.role === UserRole.SUPER_ADMIN || isCoordinatorLikeRole(user.role))
  );

  // Generar breadcrumbs basado en la ruta actual
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbs: Array<{ name: string; href: string; current: boolean }> = [];

    if (pathnames.length === 0) {
      breadcrumbs.push({ name: 'Inicio', href: '/dashboard', current: true });
    } else {
      let currentPath = '';

      pathnames.forEach((name, index) => {
        currentPath += `/${name}`;

        // Mapear nombres de rutas a nombres legibles
        const routeNames: Record<string, string> = {
          dashboard: 'Inicio',
          'academic-years': 'Años Escolares',
          create: 'Crear',
          students: 'Estudiantes',

          finance: 'Finanzas',
          settings: 'Configuración',
        };

        const displayName = routeNames[name] || name;

        breadcrumbs.push({
          name: displayName,
          href: currentPath,
          current: index === pathnames.length - 1,
        } as { name: string; href: string; current: boolean });
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const visibleYearLabel = selectedAcademicYear
    ? `${selectedAcademicYear.name}${selectedAcademicYear.state === YearState.ACTIVE ? ' (Activo)' : ''}`
    : 'Sin año seleccionado';

  return (
    <header className="bg-white/90 backdrop-blur border-b border-slate-200 px-6 py-4 shadow-sm">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        {/* Breadcrumbs */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            {breadcrumbs.map((breadcrumb, index) => (
              <li key={breadcrumb.href}>
                <div className="flex items-center">
                  {index > 0 && (
                    <svg
                      className="flex-shrink-0 h-5 w-5 text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                    </svg>
                  )}
                  <span
                    className={`ml-4 text-sm font-medium ${breadcrumb.current
                      ? 'text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    {breadcrumb.name}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </nav>
        {canUseYearSwitcher && academicYears.length > 0 && (
          <div className="flex items-center gap-3 self-start xl:self-auto">
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3 shadow-sm">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <CalendarClock className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Año escolar visible
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <select
                    className="min-w-[220px] bg-transparent text-sm font-semibold text-slate-900 outline-none"
                    value={selectedAcademicYearId || ''}
                    onChange={(e) => setSelectedAcademicYearId(e.target.value)}
                    aria-label="Año escolar visible"
                  >
                    {academicYears.map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.name} {year.state === YearState.ACTIVE ? '(Activo)' : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" />
                </div>
              </div>
            </div>

            <div
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm ${
                selectedAcademicYear?.state === YearState.ACTIVE
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700'
              }`}
              title={visibleYearLabel}
            >
              {selectedAcademicYear?.state === YearState.ACTIVE ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Activo
                </>
              ) : (
                <>
                  <History className="h-3.5 w-3.5" />
                  Histórico
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
